import { useState, useRef, useEffect } from 'react'

import { getSchoolListApi } from '@/api/neisApi'
import type { NEISGetSchoolListResponse } from '@/api/model/neisType'

interface School {
  education_office_code: string
  education_office_name: string
  school_code: string
  school_name: string
  school_type_code: string
  school_type_name: string
  city: string
  jurisdiction: string
  foundation_type: string
  post_code: string
  address: string
  detail_address: string
  school_phone: string
  school_fax: string
  school_website_url: string
}

interface NEISInputProps {
  placeholder?: string
  onSelect?: (school: School) => void
  value?: string
  onChange?: (value: string) => void
  inputClassName?: string
  schoolTypeFilter?: string
}

export default function NEISInput({
  placeholder = '학교명을 입력하세요',
  onSelect,
  value,
  onChange,
  inputClassName,
  schoolTypeFilter
}: NEISInputProps) {
  const [searchQuery, setSearchQuery] = useState(value || '')
  const [schools, setSchools] = useState<School[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [showDropdown, setShowDropdown] = useState(false)
  const [selectedIndex, setSelectedIndex] = useState(-1)
  const inputRef = useRef<HTMLInputElement>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)

  // value prop 변경을 내부 state에 동기화
  const prevValueRef = useRef<string | undefined>(undefined)
  useEffect(() => {
    if (value !== undefined && value !== prevValueRef.current) {
      setSearchQuery(value)
      prevValueRef.current = value
    }
  }, [value])

  // 외부 클릭 감지
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setShowDropdown(false)
        setSelectedIndex(-1)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  // 학교 검색 API 호출
  const searchSchools = async (query: string) => {
    if (!query.trim()) {
      setSchools([])
      setShowDropdown(false)
      return
    }

    setIsLoading(true)
    try {
      const response: NEISGetSchoolListResponse = await getSchoolListApi({
        school_name: query.trim()
      })

      if (response.data && response.data.schools) {
        // 학교 타입 필터링 적용
        let filteredSchools = response.data.schools
        if (schoolTypeFilter) {
          filteredSchools = response.data.schools.filter(school => school.school_type_name === schoolTypeFilter)
        }

        setSchools(filteredSchools)
        setShowDropdown(true)
        setSelectedIndex(-1)
      } else {
        setSchools([])
        setShowDropdown(false)
      }
    } catch (error) {
      console.error('학교 검색 중 오류가 발생했습니다:', error)
      setSchools([])
      setShowDropdown(false)
    } finally {
      setIsLoading(false)
    }
  }

  // 검색 실행
  const handleSearch = () => {
    searchSchools(searchQuery)
  }

  // 입력값 변경
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value
    setSearchQuery(newValue)
    onChange?.(newValue)

    // 입력값이 비어있으면 드롭다운 숨기기
    if (!newValue.trim()) {
      setShowDropdown(false)
      setSchools([])
    }
  }

  // 엔터키 처리
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      if (selectedIndex >= 0 && schools[selectedIndex]) {
        selectSchool(schools[selectedIndex])
      } else {
        handleSearch()
      }
    } else if (e.key === 'ArrowDown') {
      e.preventDefault()
      if (showDropdown && schools.length > 0) {
        setSelectedIndex(prev => (prev + 1) % schools.length)
      }
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      if (showDropdown && schools.length > 0) {
        setSelectedIndex(prev => (prev <= 0 ? schools.length - 1 : prev - 1))
      }
    } else if (e.key === 'Escape') {
      setShowDropdown(false)
      setSelectedIndex(-1)
    }
  }

  // 학교 선택
  const selectSchool = (school: School) => {
    setSearchQuery(school.school_name)
    setShowDropdown(false)
    setSelectedIndex(-1)
    onSelect?.(school)
    // onChange는 호출하지 않음 - onSelect에서 처리
  }

  return (
    <div className='relative w-full'>
      {/* 검색 입력창 */}
      <div className='relative'>
        <input
          ref={inputRef}
          type='text'
          value={searchQuery}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          className={inputClassName}
        />

        {/* 검색 아이콘 버튼 */}
        <button
          onClick={handleSearch}
          disabled={isLoading}
          className='absolute right-3 top-1/2 transform -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600 disabled:text-gray-300'
        >
          {isLoading ? (
            <div className='w-5 h-5 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin'></div>
          ) : (
            <svg className='w-5 h-5' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth={2}
                d='M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z'
              />
            </svg>
          )}
        </button>
      </div>

      {/* 드롭다운 리스트 */}
      {showDropdown && schools.length > 0 && (
        <div
          ref={dropdownRef}
          className='absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto'
        >
          {schools.map((school, index) => (
            <div
              key={`${school.school_code}-${index}`}
              onClick={() => selectSchool(school)}
              className={`px-4 py-3 cursor-pointer border-b border-gray-100 last:border-b-0 hover:bg-gray-50 ${
                selectedIndex === index ? 'bg-blue-50 text-blue-700' : 'text-gray-900'
              }`}
            >
              <div className='font-medium'>{school.school_name}</div>
              <div className='text-sm text-gray-500 mt-1'>
                {school.education_office_name} · {school.city}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* 검색 결과 없음 */}
      {showDropdown && schools.length === 0 && !isLoading && searchQuery.trim() && (
        <div
          ref={dropdownRef}
          className='absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg'
        >
          <div className='px-4 py-3 text-gray-500 text-center'>검색 결과가 없습니다.</div>
        </div>
      )}
    </div>
  )
}
